import { useEffect, useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { clinicService } from "../services/api";
import { useClinicDocumentMeta } from "../hooks/useClinicDocumentMeta";
import CareLineLayout from "../components/careline/CareLineLayout";
import { CareLineContent } from "./CareLine";
import { isSessionForClinic, getTenantDashboardPath } from "../utils/tenantAuth";

/**
 * Public landing: `/careline` (fixedSlug) = demo tenant only; `/:slug` = real clinic from API.
 */
export default function ClinicLanding({ fixedSlug }) {
    const { slug: paramSlug } = useParams();
    const slug = fixedSlug ?? paramSlug;
    const [state, setState] = useState("loading");
    const [clinic, setClinic] = useState(null);

    useClinicDocumentMeta({
        title: clinic?.nama_klinik ? `${clinic.nama_klinik}` : "Klinik",
        faviconUrl: clinic?.logo_url || null,
        enabled: state === "ok" && !!clinic,
    });

    useEffect(() => {
        if (!slug) {
            setState("notfound");
            return;
        }
        let cancelled = false;
        clinicService
            .getBySlug(slug)
            .then((res) => {
                if (cancelled) return;
                if (res.data?.success) {
                    setClinic(res.data.data);
                    setState("ok");
                } else {
                    setState("notfound");
                }
            })
            .catch(() => {
                if (!cancelled) setState("notfound");
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    const sessionRedirect = useMemo(() => {
        if (state !== "ok" || !clinic || !slug) return null;
        const token = localStorage.getItem("token");
        if (!token) return null;
        let user = null;
        try {
            user = JSON.parse(localStorage.getItem("user") || "null");
        } catch {
            return null;
        }
        if (!user || !isSessionForClinic(user, clinic, slug)) return null;
        return getTenantDashboardPath(user, slug);
    }, [state, clinic, slug]);

    if (state === "loading") {
        return (
            <CareLineLayout slug={slug} activePage="" logo={null} primaryColor="#06B6D4">
                <div className="flex flex-1 min-h-[50vh] items-center justify-center text-slate-500 text-sm font-jakarta">
                    Memuat…
                </div>
            </CareLineLayout>
        );
    }

    if (state === "notfound") {
        return (
            <CareLineLayout slug={slug} activePage="" logo={null} primaryColor="#06B6D4">
                <div className="flex flex-1 flex-col items-center justify-center px-6 py-20 text-center font-jakarta">
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">
                        404 — Klinik tidak ditemukan
                    </h1>
                    <p className="text-slate-500 text-sm max-w-md mb-8">
                        Slug ini tidak terdaftar. Periksa URL atau hubungi administrator
                        klinik.
                    </p>
                    <Link
                        to="/qease"
                        className="px-6 py-3 rounded-full text-white text-sm font-semibold bg-gradient-to-r from-[#06B6D4] to-[#0891B2] shadow-lg shadow-cyan-500/25"
                    >
                        Kembali ke beranda
                    </Link>
                </div>
            </CareLineLayout>
        );
    }

    if (sessionRedirect) {
        return <Navigate to={sessionRedirect} replace />;
    }

    return (
        <CareLineLayout
            slug={slug}
            activePage="layanan"
            logo={clinic?.logo_url}
            clinicName={clinic?.nama_klinik}
            primaryColor={clinic?.primary_color}
        >
            <CareLineContent
                slug={slug}
                logo={clinic?.logo_url}
                carouselImages={clinic?.carousel_images}
            />
        </CareLineLayout>
    );
}
